import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  BarChart3, Package, TrendingDown, Calendar, Loader, DollarSign, ArrowDownRight, Clock, Target
} from 'lucide-react';
import { useTheme } from './ThemeContext';

export default function InventoryReportsPage({ companyId }) {
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [transactions, setTransactions] = useState([]);
  const [usageSummary, setUsageSummary] = useState([]);
  const [costSummary, setCostSummary] = useState({ totalUsed: 0, totalCost: 0 });
  const [burnRateItems, setBurnRateItems] = useState([]);
  const [monthlyBudget, setMonthlyBudget] = useState(0);
  const [monthSpend, setMonthSpend] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) loadReportData();
  }, [companyId, dateRange]);

  const loadReportData = async () => {
    try {
      setLoading(true);

      const sinceDate = new Date();
      sinceDate.setDate(sinceDate.getDate() - parseInt(dateRange));
      const sinceDateStr = sinceDate.toISOString();

      // Load usage transactions
      const { data: txns } = await supabase
        .from('inventory_transactions')
        .select('*, inventory_items(name, category, unit_cost, unit_of_measure)')
        .eq('company_id', companyId)
        .in('transaction_type', ['usage', 'job_deduction'])
        .gte('created_at', sinceDateStr)
        .order('created_at', { ascending: false });

      setTransactions(txns || []);

      // Build usage summary by item
      const itemMap = {};
      (txns || []).forEach(txn => {
        const itemId = txn.inventory_item_id;
        if (!itemMap[itemId]) {
          itemMap[itemId] = {
            id: itemId,
            name: txn.inventory_items?.name || 'Unknown',
            category: txn.inventory_items?.category || '',
            unit_cost: txn.inventory_items?.unit_cost || 0,
            unit: txn.inventory_items?.unit_of_measure || 'ea',
            total_used: 0,
            job_count: 0,
            job_ids: new Set()
          };
        }
        itemMap[itemId].total_used += Math.abs(txn.quantity_change || 0);
        if (txn.job_id) itemMap[itemId].job_ids.add(txn.job_id);
      });

      const summary = Object.values(itemMap)
        .map(item => ({
          ...item,
          job_count: item.job_ids.size,
          total_cost: item.total_used * item.unit_cost
        }))
        .sort((a, b) => b.total_used - a.total_used);

      setUsageSummary(summary);

      const totalUsed = summary.reduce((sum, i) => sum + i.total_used, 0);
      const totalCost = summary.reduce((sum, i) => sum + i.total_cost, 0);
      setCostSummary({ totalUsed, totalCost });

      // Load burn rate data — need current stock + 30-day usage
      try {
        const { data: defaultLoc } = await supabaseData
          .from('locations')
          .select('id')
          .eq('company_id', companyId)
          .eq('is_default', true)
          .single();

        if (defaultLoc) {
          const { data: allItems } = await supabaseData
            .from('inventory_items')
            .select('id, name, reorder_point, unit_cost')
            .eq('company_id', companyId)
            .eq('is_active', true);

          const { data: stockData } = await supabaseData
            .from('inventory_stock')
            .select('inventory_item_id, quantity_on_hand')
            .eq('company_id', companyId)
            .eq('location_id', defaultLoc.id);

          const stockMap = {};
          (stockData || []).forEach(s => { stockMap[s.inventory_item_id] = s.quantity_on_hand || 0; });

          // 30-day usage for burn rate (always 30 days regardless of dateRange)
          const thirtyAgo = new Date();
          thirtyAgo.setDate(thirtyAgo.getDate() - 30);
          const { data: usageTxns } = await supabaseData
            .from('inventory_transactions')
            .select('inventory_item_id, quantity_change')
            .eq('company_id', companyId)
            .in('transaction_type', ['usage', 'job_deduction'])
            .gte('created_at', thirtyAgo.toISOString());

          const usageMap30 = {};
          (usageTxns || []).forEach(t => {
            usageMap30[t.inventory_item_id] = (usageMap30[t.inventory_item_id] || 0) + Math.abs(t.quantity_change || 0);
          });

          const burns = (allItems || [])
            .map(item => {
              const qty = stockMap[item.id] || 0;
              const used30 = usageMap30[item.id] || 0;
              const dailyBurn = used30 / 30;
              const daysRemaining = dailyBurn > 0 ? Math.round(qty / dailyBurn) : null;
              return { ...item, qty, dailyBurn, daysRemaining, used30 };
            })
            .filter(item => item.used30 > 0)
            .sort((a, b) => {
              if (a.daysRemaining === null) return 1;
              if (b.daysRemaining === null) return -1;
              return a.daysRemaining - b.daysRemaining;
            });

          setBurnRateItems(burns);
        }
      } catch (e) {
        console.error('Burn rate load error:', e);
      }

      // Load budget data
      try {
        const { data: company } = await supabaseData
          .from('companies')
          .select('inventory_monthly_budget')
          .eq('id', companyId)
          .single();

        setMonthlyBudget(company?.inventory_monthly_budget || 0);

        // This month's spend
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const { data: monthTxns } = await supabaseData
          .from('inventory_transactions')
          .select('quantity_change, inventory_items(unit_cost)')
          .eq('company_id', companyId)
          .in('transaction_type', ['usage', 'job_deduction'])
          .gte('created_at', monthStart);

        const spend = (monthTxns || []).reduce((sum, t) => {
          return sum + Math.abs(t.quantity_change || 0) * (t.inventory_items?.unit_cost || 0);
        }, 0);
        setMonthSpend(spend);
      } catch (e) {
        console.error('Budget load error:', e);
      }

    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: colors.textMuted }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading reports...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
          <BarChart3 size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
          Inventory Reports
        </h1>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            minWidth: '160px'
          }}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.25rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <ArrowDownRight size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
            Items Used
          </div>
          <div style={{ color: colors.text, fontSize: '1.75rem', fontWeight: 'bold' }}>
            {costSummary.totalUsed}
          </div>
        </div>

        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.25rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <DollarSign size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
            Materials Cost
          </div>
          <div style={{ color: colors.text, fontSize: '1.75rem', fontWeight: 'bold' }}>
            ${costSummary.totalCost.toFixed(2)}
          </div>
        </div>

        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.25rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <Package size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
            Unique Items
          </div>
          <div style={{ color: colors.text, fontSize: '1.75rem', fontWeight: 'bold' }}>
            {usageSummary.length}
          </div>
        </div>
      </div>

      {/* Budget vs Actual */}
      {monthlyBudget > 0 && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '1.25rem',
          border: `1px solid ${colors.border}`,
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: '0 0 1rem', color: colors.text, fontSize: '1.1rem' }}>
            <Target size={18} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
            Monthly Budget
          </h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
              ${monthSpend.toFixed(2)} spent of ${monthlyBudget.toFixed(2)}
            </span>
            <span style={{
              color: monthSpend > monthlyBudget ? colors.error : monthSpend > monthlyBudget * 0.8 ? colors.warning : colors.success,
              fontWeight: 'bold',
              fontSize: '0.85rem'
            }}>
              {Math.round((monthSpend / monthlyBudget) * 100)}%
            </span>
          </div>
          <div style={{
            height: '10px',
            background: colors.bgInput,
            borderRadius: '5px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min(100, (monthSpend / monthlyBudget) * 100)}%`,
              background: monthSpend > monthlyBudget ? colors.error : monthSpend > monthlyBudget * 0.8 ? colors.warning : colors.success,
              borderRadius: '5px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '0.5rem' }}>
            ${Math.max(0, monthlyBudget - monthSpend).toFixed(2)} remaining this month
          </div>
        </div>
      )}

      {/* Burn Rate / Days Until Stockout */}
      {burnRateItems.length > 0 && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          marginBottom: '2rem'
        }}>
          <div style={{
            padding: '1.25rem',
            borderBottom: `1px solid ${colors.border}`
          }}>
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
              <Clock size={18} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
              Burn Rate — Days Until Stockout
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1.25rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Item</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>On Hand</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Daily Burn</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>30d Used</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1.25rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Days Left</th>
                </tr>
              </thead>
              <tbody>
                {burnRateItems.map(item => {
                  const daysColor = item.daysRemaining === null ? colors.textMuted
                    : item.daysRemaining <= 3 ? colors.error
                    : item.daysRemaining <= 7 ? colors.warning
                    : item.daysRemaining <= 14 ? '#f59e0b'
                    : colors.success;
                  return (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '0.75rem 1.25rem', color: colors.text, fontWeight: '500' }}>{item.name}</td>
                      <td style={{ padding: '0.75rem 0.5rem', color: colors.text, textAlign: 'right' }}>{item.qty}</td>
                      <td style={{ padding: '0.75rem 0.5rem', color: colors.textMuted, textAlign: 'right' }}>{item.dailyBurn.toFixed(1)}/day</td>
                      <td style={{ padding: '0.75rem 0.5rem', color: colors.textMuted, textAlign: 'right' }}>{item.used30}</td>
                      <td style={{ padding: '0.75rem 1.25rem', textAlign: 'right', fontWeight: 'bold', color: daysColor }}>
                        {item.daysRemaining !== null ? `${item.daysRemaining}d` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage by Item */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1.25rem',
          borderBottom: `1px solid ${colors.border}`
        }}>
          <h2 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
            <TrendingDown size={18} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
            Usage by Item
          </h2>
        </div>

        {usageSummary.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textMuted }}>
            No inventory usage recorded in this period
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '500px'
            }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1.25rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Item</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Qty Used</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Jobs</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1.25rem', color: colors.textMuted, fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {usageSummary.map(item => (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '0.75rem 1.25rem', color: colors.text, fontWeight: '500' }}>
                      {item.name}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                      {item.category || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: colors.text, textAlign: 'right', fontWeight: 'bold' }}>
                      {item.total_used} {item.unit}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: colors.textMuted, textAlign: 'right' }}>
                      {item.job_count}
                    </td>
                    <td style={{ padding: '0.75rem 1.25rem', color: colors.text, textAlign: 'right', fontWeight: 'bold' }}>
                      {item.total_cost > 0 ? `$${item.total_cost.toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          padding: '1.25rem',
          borderBottom: `1px solid ${colors.border}`
        }}>
          <h2 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
            <Calendar size={18} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
            Recent Transactions
          </h2>
        </div>

        {transactions.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textMuted }}>
            No transactions in this period
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {transactions.slice(0, 50).map(txn => (
              <div key={txn.id} style={{
                padding: '0.75rem 1.25rem',
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ color: colors.text, fontSize: '0.9rem' }}>
                    {txn.inventory_items?.name || 'Unknown'}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                    {new Date(txn.created_at).toLocaleDateString()} — {txn.notes || 'Job usage'}
                  </div>
                </div>
                <div style={{
                  color: colors.error,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {txn.quantity_change}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
